import { useState } from "react";
import { Link } from "wouter";
import { Tag, Calendar, MapPin, ChevronDown, ChevronUp, CheckCircle } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useSEO } from "@/hooks/use-seo";
import { useQuery } from "@tanstack/react-query";

interface RedeemedDeal {
  redemptionId: number;
  redeemedAt: string;
  dealId: number;
  title: string;
  businessName: string;
  description: string;
  town: string;
  category: string;
  imageUrl: string | null;
  redemptionInstructions: string | null;
  endDate: string | null;
  slug: string | null;
}

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function DealCard({ deal }: { deal: RedeemedDeal }) {
  const [open, setOpen] = useState(false);
  const isExpired = deal.endDate ? new Date(deal.endDate) < new Date() : false;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      {deal.imageUrl ? (
        <div className="relative h-36 overflow-hidden">
          <img src={deal.imageUrl} alt={deal.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          {isExpired && (
            <div className="absolute top-2 right-2 bg-slate-700/80 text-white text-xs font-semibold px-2 py-0.5 rounded-full">Expired</div>
          )}
        </div>
      ) : (
        <div className="h-24 bg-gradient-to-br from-secondary/20 to-amber-50 flex items-center justify-center">
          <Tag size={28} className="text-secondary" />
          {isExpired && (
            <span className="absolute top-2 right-2 bg-slate-700/80 text-white text-xs font-semibold px-2 py-0.5 rounded-full">Expired</span>
          )}
        </div>
      )}

      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-bold text-slate-800 text-sm leading-tight line-clamp-2">{deal.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">{deal.businessName}</p>
          </div>
          <CheckCircle size={18} className="flex-shrink-0 text-emerald-500 mt-0.5" />
        </div>

        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="flex items-center gap-1"><MapPin size={11} />{deal.town}</span>
          <span className="flex items-center gap-1"><Calendar size={11} />Redeemed {new Date(deal.redeemedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
        </div>

        {deal.redemptionInstructions && (
          <div>
            <button
              onClick={() => setOpen((o) => !o)}
              className="flex items-center gap-1.5 text-xs text-primary font-semibold"
            >
              {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              How to redeem
            </button>
            {open && (
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 leading-relaxed">
                {deal.redemptionInstructions}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Link href={deal.slug ? `/deals/${deal.slug}` : `/deals/${deal.dealId}`} className="flex-1">
            <button className="w-full text-xs font-semibold text-primary border border-primary/30 bg-primary/5 py-2 rounded-lg hover:bg-primary/10 transition-colors">
              View deal
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function MyDeals() {
  const { user } = useAuth();
  useSEO({ title: "My Deals — Ayrshire Hub" });

  const { data, isLoading } = useQuery<{ items: RedeemedDeal[] }>({
    queryKey: ["my-redeemed-deals"],
    queryFn: async () => {
      const res = await fetch("/api/my-redeemed-deals", { headers: authHeaders() });
      if (!res.ok) throw new Error("Failed to load");
      return res.json() as Promise<{ items: RedeemedDeal[] }>;
    },
    enabled: !!user,
    staleTime: 30000,
  });

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 max-w-sm w-full text-center">
          <Tag size={32} className="text-secondary mx-auto mb-3" />
          <h2 className="text-lg font-bold text-slate-800 mb-2">Sign in to see your deals</h2>
          <p className="text-sm text-slate-500 mb-5">Log in to view deals you've redeemed.</p>
          <Link href="/login">
            <button className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-2.5 rounded-xl transition-colors">Sign in</button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-400 mb-5">
          <Link href="/" className="hover:text-primary transition-colors">Home</Link>
          <span>/</span>
          <span className="text-slate-600 font-medium">My Deals</span>
        </nav>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
            <Tag size={20} className="text-secondary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">My Deals</h1>
            <p className="text-sm text-slate-500">Deals you've redeemed</p>
          </div>
        </div>

        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden animate-pulse">
                <div className="h-36 bg-slate-200" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-3/4" />
                  <div className="h-3 bg-slate-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoading && (!data?.items || data.items.length === 0) && (
          <div className="text-center py-16">
            <Tag size={40} className="text-slate-300 mx-auto mb-4" />
            <h3 className="text-slate-600 font-semibold mb-2">No redeemed deals yet</h3>
            <p className="text-sm text-slate-400 mb-5">Browse deals from local businesses and redeem them here.</p>
            <Link href="/deals">
              <button className="bg-primary hover:bg-primary/90 text-white font-bold px-6 py-2.5 rounded-xl transition-colors text-sm">
                Browse deals
              </button>
            </Link>
          </div>
        )}

        {!isLoading && data?.items && data.items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {data.items.map((deal) => (
              <DealCard key={deal.redemptionId} deal={deal} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
